import { SubHeading, SummaryCard } from "@certn/cascadia-react";
import { Link } from "@certn/cascadia";
import { CascadiaJsonFormContext } from "@certn/forms";
import { SummaryItem } from "@certn/forms/src/components/SummaryItem";
import { Fragment, useContext } from "react";
import { CUSTOM_QUESTIONNAIRE_KEY } from "./types";
import { CustomQuestionnaireLayoutContext } from "../../layouts/CustomQuestionnaireLayoutContext";
import { useTranslation } from "react-i18next";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { NotStarted } from "../../layouts/NotStarted";

export function CustomQuestionnaireSummaryControl() {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const { basePath = "apply" } = useApplication();
  const { sectionLabel, templateData } = useContext(
    CustomQuestionnaireLayoutContext
  );
  const { values } = useContext(CascadiaJsonFormContext);

  const hasRequiredClaims = (templateData?.templates ?? []).some((template) =>
    template.template_input_claims.some((claim) => claim.is_optional === false)
  );

  const groupedAnswers = (templateData?.templates ?? [])
    .map((template) => ({
      templateName: template.name,
      answers: template.template_input_claims
        .filter(
          (claim) =>
            values[CUSTOM_QUESTIONNAIRE_KEY] &&
            claim.name in values[CUSTOM_QUESTIONNAIRE_KEY]
        )
        .map((claim) => {
          const type = claim.type;
          const answer = values[CUSTOM_QUESTIONNAIRE_KEY]?.[claim.name];

          if (type === "bool") {
            const value =
              answer === "true"
                ? t("Yes")
                : answer === "false"
                  ? t("No")
                  : answer;

            return {
              name: claim.name,
              label: claim.label,
              value,
            };
          }

          const constraints = claim.constraints
            ? claim.constraints[0]
            : undefined;
          const selectableOptions = constraints?.selectable_options || [];

          const value =
            selectableOptions.find((o) => o.value === answer)?.label ?? answer;

          return {
            name: claim.name,
            label: claim.label,
            value,
          };
        }),
    }))
    .filter((group) => group.answers.length > 0);

  if (groupedAnswers.length === 0 && hasRequiredClaims) {
    return (
      <SummaryCard
        header={sectionLabel}
        actions={
          <Link
            href={`/${basePath}/${caseId}/custom-questionnaire`}
            textColor="text-weak"
            testID="custom-questionnaire-edit-button"
          >
            {t("Edit")}
          </Link>
        }
      >
        <NotStarted sectionKey={"custom-questionnaire"} showErrors={false} />
      </SummaryCard>
    );
  }

  return (
    <SummaryCard
      header={sectionLabel}
      actions={
        <Link
          href={`/${basePath}/${caseId}/custom-questionnaire`}
          textColor="text-weak"
          testID="custom-questionnaire-edit-button"
        >
          {t("Edit")}
        </Link>
      }
    >
      {groupedAnswers.map((group) => (
        <Fragment key={group.templateName}>
          <SubHeading>{group.templateName}</SubHeading>
          {group.answers.map((answer) => (
            <SummaryItem key={answer.name} label={answer.label ?? answer.name}>
              {String(answer.value ?? "")}
            </SummaryItem>
          ))}
        </Fragment>
      ))}
    </SummaryCard>
  );
}
